import React from "react";
import { ArrowPathIcon, ClockIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";

export function formatSceneDuration(seconds) {
  if (seconds == null) return null;
  const total = Math.max(0, Math.round(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function resolveVisualStatus(imageUrl, status) {
  if (status) return status;
  if (imageUrl) return "completed";
  return "generating";
}

const overlayPillClass =
  "inline-flex items-center gap-1 rounded-md bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white";

const captionTagStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(2px)",
};

const topCaptionStyle = {
  ...captionTagStyle,
  color: "#c4ff4d",
};

const bottomCaptionStyle = {
  ...captionTagStyle,
  color: "#4fd7ee",
};

const topCaptionClass =
  "inline-block rounded-md px-3 py-1.5 text-center text-base font-bold uppercase leading-tight tracking-wide sm:text-lg";

const bottomCaptionClass =
  "inline-block max-w-full rounded-md px-2.5 py-1 text-center text-xs font-medium leading-snug sm:text-sm";

const SpinnerIcon = () => (
  <svg
    className="w-5 h-5 animate-spin text-theme-primary opacity-80"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

/**
 * Fixed 9:16 scene preview with overlaid caption, badges, and visual generation states.
 */
const SceneVisualPreview = ({
  scene,
  sceneNumber,
  imageUrl,
  status,
  onRetryVisual,
  onRegenerateVisual,
  onClick,
  className = "",
}) => {
  const visualStatus = resolveVisualStatus(imageUrl, status);
  const duration = formatSceneDuration(scene?.duration_seconds);
  const onScreenCaption = scene?.on_screen_text?.trim() || null;
  const spokenText = scene?.spoken_text?.trim() || null;
  const num = sceneNumber ?? scene?.scene_number;
  const regenerateTooltipId = num != null ? `scene-regenerate-${num}` : null;
  const showImage = visualStatus === "completed" && imageUrl;
  const showGeneratingOverlay = visualStatus === "generating" && imageUrl;

  const frameClass =
    "group/preview relative w-60 sm:w-[16.5rem] flex-shrink-0 aspect-[9/16] rounded-lg overflow-hidden border border-theme-secondary bg-theme-secondary bg-opacity-50";

  const inner = (
    <>
      {showImage || (visualStatus === "generating" && imageUrl) ? (
        <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : null}

      {showGeneratingOverlay ? (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-theme-main/60">
          <SpinnerIcon />
          <span className="text-[10px] font-medium uppercase tracking-wide text-theme-text opacity-80">
            Regenerating…
          </span>
        </div>
      ) : null}

      <div className="absolute inset-x-0 top-0 z-20 px-2 pt-2">
        <div className="flex items-start justify-between gap-2">
          {num != null ? (
            <span className={`${overlayPillClass} tabular-nums`}>
              #{num}
              {onRegenerateVisual && imageUrl ? (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRegenerateVisual();
                    }}
                    className="inline-flex shrink-0 opacity-70 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-white rounded"
                    data-tooltip-id={regenerateTooltipId}
                    data-tooltip-content="Regenerate"
                    aria-label="Regenerate"
                  >
                    <ArrowPathIcon className="h-3 w-3" aria-hidden="true" />
                  </button>
                  <Tooltip id={regenerateTooltipId} place="bottom" className="z-50 !text-xs" />
                </>
              ) : null}
            </span>
          ) : (
            <span className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
          )}
          {duration ? (
            <span className={`${overlayPillClass} tabular-nums`}>
              <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {duration}
            </span>
          ) : null}
        </div>
        {onScreenCaption ? (
          <div className="mt-1.5 flex justify-center px-1">
            <p className={topCaptionClass} style={topCaptionStyle}>
              {onScreenCaption}
            </p>
          </div>
        ) : null}
      </div>

      {spokenText && (showImage || (visualStatus === "generating" && imageUrl)) ? (
        <div className="absolute inset-x-2 bottom-2 z-20 flex justify-center">
          <p className={bottomCaptionClass} style={bottomCaptionStyle}>
            {spokenText}
          </p>
        </div>
      ) : null}

      {visualStatus !== "completed" && visualStatus !== "generating" ? (
        <div className={`absolute inset-0 flex items-center justify-center px-3 ${onScreenCaption ? "pt-24" : "pt-10"} pb-4`}>
          {visualStatus === "queued" ? (
            <div className="flex max-h-full flex-col items-center justify-center gap-2 text-center">
              <span className="text-[10px] font-medium uppercase tracking-wide text-theme-text opacity-50">
                Queued…
              </span>
            </div>
          ) : null}

          {visualStatus === "failed" ? (
            <div className="flex flex-col items-center justify-center gap-2 text-center">
              <span className="text-[10px] font-medium text-theme-text opacity-60">
                Couldn&apos;t generate
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetryVisual?.();
                }}
                className="rounded border border-theme-secondary px-2 py-0.5 text-[10px] text-theme-text opacity-70 hover:bg-theme-secondary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-theme-primary"
              >
                Retry
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {visualStatus === "generating" && !imageUrl ? (
        <div className={`absolute inset-0 flex items-center justify-center px-3 ${onScreenCaption ? "pt-24" : "pt-10"} pb-4`}>
          <div className="flex max-h-full flex-col items-center justify-center gap-2 text-center">
            <SpinnerIcon />
            <span className="text-[10px] font-medium uppercase tracking-wide text-theme-text opacity-60">
              Generating visual…
            </span>
          </div>
        </div>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${frameClass} cursor-pointer hover:border-theme-primary hover:border-opacity-60 focus:outline-none focus:ring-2 focus:ring-theme-primary ${className}`.trim()}
        aria-label={`Edit scene ${num ?? ""}`.trim()}
      >
        {inner}
      </button>
    );
  }

  return <div className={`${frameClass} ${className}`.trim()}>{inner}</div>;
};

export default SceneVisualPreview;
